import type { TransactionRecord } from '@/types/Transaction'
import { TransactionPartyModel } from '@/models/TransactionPartyModel'
import { usePersonStore } from '@/stores/v2/person'
import { useOrganizationStoreV2 } from '@/stores/v2/organization'

export class TransactionModel {
  private personStore = usePersonStore()
  private organizationStore = useOrganizationStoreV2()

  constructor(private transaction: TransactionRecord) {}

  get isComplete() {
    return this.transaction.transaction_status === 'completed'
  }

  get totalItems() {
    return this.transaction.items.reduce((sum, item) => sum + item.quantity, 0)
  }

  get displayName() {
    return `#${this.transaction.transaction_number} - ${this.party.name}`
  }

  get party() {
    return new TransactionPartyModel(this.transaction)
  }

  async getParty() {
    let person = undefined
    let organization = undefined

    console.log('Getting party data for transaction:', this.transaction)

    if (this.transaction.personId) {
      console.log('Fetching person with ID:', this.transaction.personId)
      person = await this.personStore.get(this.transaction.personId)
      console.log('Fetched person:', person)
    }
    if (this.transaction.organizationId) {
      console.log('Fetching organization with ID:', this.transaction.organizationId)
      organization = await this.organizationStore.get(this.transaction.organizationId)
      console.log('Fetched organization:', organization)
    }

    return new TransactionPartyModel(
      this.transaction,
      person || undefined,
      organization || undefined,
    )
  }

  get totalWeight() {
    return (this.transaction.items || [])
      .reduce((total, item) => {
        return total + (item.weight?.net || 0)
      }, 0)
      .toFixed(2)
  }

  get totalAmount() {
    return (this.transaction.items || [])
      .reduce((total, item) => {
        const price = Number(item.price_per_lb) || 0
        const weight = Number(item.weight?.net) || 0
        return total + parseFloat((price * weight).toFixed(2))
      }, 0)
      .toFixed(2)
  }

  get totalAmountNumber() {
    return (this.transaction.items || []).reduce((total, item) => {
      const price = Number(item.price_per_lb) || 0
      const weight = Number(item.weight?.net) || 0
      return total + parseFloat((price * weight).toFixed(2))
    }, 0)
  }

  get isFormValid() {
    return (
      !!this.transaction &&
      !!this.transaction.transaction_date &&
      (this.transaction.items || []).length > 0 &&
      (this.transaction.items || []).every(
        (item) => !!item.commodity_type_id && item.weight?.net && item.weight.net > 0,
      )
    )
  }

  static formatDate(date: Date): string {
    if (!date) return ''
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date)
  }

  static formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value)
  }

  static getStatusLabel(status: string): string {
    const statuses = [
      { label: 'Pending', value: 'pending' },
      { label: 'Submitted', value: 'submitted' },
      { label: 'Completed', value: 'completed' },
      { label: 'Cancelled', value: 'cancelled' },
      { label: 'Editing', value: 'editing' },
    ]
    const statusObj = statuses.find((s) => s.value === status)
    return statusObj ? statusObj.label : status
  }

  static getStatusSeverity(status: string): string {
    switch (status) {
      case 'submitted':
        return 'info'
      case 'completed':
        return 'success'
      case 'pending':
        return 'warning'
      case 'editing':
        return 'warning'
      case 'cancelled':
        return 'danger'
      default:
        return 'info'
    }
  }

  static fromFirestore(data: TransactionRecord): TransactionRecord {
    return {
      ...data,
      id: data.id ?? crypto.randomUUID(), // fallback
    }
  }
}
